/**
 * Day-over-day movement of assigned licences, derived client-side from the daily snapshot series —
 * the API stays untouched (principal 31/7: "อยากเห็นการเปลี่ยนแปลงของ License ที่ใช้งานในแต่ละวัน").
 *
 * The one rule that matters: a delta exists ONLY between two snapshots on directly consecutive
 * calendar days. The first day of a range has no baseline, and a hole in the series (a missed
 * 23:00 tick is a PERMANENT gap by design — no backfill) must not let one bar silently blend
 * several days of movement into a single "daily" change. Both cases are `null`, rendered by
 * DivergingBarChart as a no-data stub, never 0.
 */
import type { PoolReportDay } from "@/api/types";

const DAY_MS = 86_400_000;

/** True when `next` is exactly the calendar day after `prev` (both "YYYY-MM-DD"; UTC-midnight math,
 *  same idiom as PoolReportsPage's own date arithmetic — no host-timezone dependence). */
function isNextCalendarDay(prev: string, next: string): boolean {
  return (
    new Date(`${prev}T00:00:00Z`).getTime() + DAY_MS === new Date(`${next}T00:00:00Z`).getTime()
  );
}

export interface AssignedDeltaPoint {
  snapshot_date: string;
  /** assigned(today) − assigned(yesterday), or null when yesterday's snapshot is not in the data. */
  delta: number | null;
}

/** `days` must be chronological ASC (the page reverses the API's DESC order before calling). */
export function assignedDeltaSeries(days: readonly PoolReportDay[]): AssignedDeltaPoint[] {
  return days.map((d, i) => {
    const prev = i > 0 ? days[i - 1] : undefined;
    return {
      snapshot_date: d.snapshot_date,
      delta:
        prev && isNextCalendarDay(prev.snapshot_date, d.snapshot_date)
          ? d.assigned - prev.assigned
          : null,
    };
  });
}
